import { useEffect, useState, useCallback, useRef } from "react";
import { format, parseISO } from "date-fns";
import isEqual from "react-fast-compare";
import ActiveTableFileEditor from "./Grid/ActiveTableFileEditor";
import { cloneDeep, orderBy, pick, merge, omit } from "lodash-es";
import { isDate } from "../../utils/formatters/dateFormatter";
import { mergeColumnsAndQuery } from "./activeTableHelpers";
import usePrevious from "../../utils/usePrevious";
import { isActiveTableColumnTypeFile } from "../../utils/activeTable";
import formatter from "../../utils/formatters/formatter";
import { produce } from "immer";
import ActiveTableDropdown from "./Grid/ActiveTableDropdown";
import { activeTableDateFormatString } from "../../utils/constants/constants";
import FileRenderer from "./Grid/FileRenderer";
import DeleteActiveTableRow from "./RowControls/DeleteActiveTableRow";
import RowControls from "./RowControls/RowControls";
import { getColoredColumnEdgesByOperator } from "../TableView/tableCellFormatter";

/**
 * @typedef {import('@ag-grid-community/core').ColDef} ColDef
 */

export default function useActiveTableSettings(
  tableConfig,
  user = { groups: [] },
  queryFields,
  dataSourceUuid,
  handleRowChangeRef
) {
  // To always call the latest-rendered version of the callback.
  const handleRowChange = useCallback(
    (...args) => handleRowChangeRef.current(...args),
    [handleRowChangeRef]
  );
  const previousActiveTableRootRef = useRef({});
  const [activeTableRoot, setActiveTableRoot] = useState({});

  // Table Columns for Configs
  const [columns, setColumns] = useState([]);

  // Last time table config was updated (refs to avoid re-render loops)
  const prevConfigRef = useRef(null);

  // Last time table config was updated, translated versions
  const prevTranslatedConfigRef = useRef(null);
  // Errors object similar Laravel's API error response format.
  // Keys represent field names, and the values are arrays of error messages.
  const [columnErrorsObject, setColumnErrorsObject] = useState({});

  function setColumnError(key, value) {
    setColumnErrorsObject((columnErrorsObject) =>
      produce(columnErrorsObject, (draft = {}) => {
        if (value) {
          if (Array.isArray(value)) {
            draft[key] = value;
          } else {
            if (!columnErrorsObject[key]) {
              draft[key] = [];
            }
            // Trick to make sure the error object does not get mutated
            // unnecessarily if it would result in no changes.
            draft[key].splice(0, Infinity, value);
          }
        } else {
          delete draft[key];
        }
        return draft;
      })
    );
  }

  const defaultRow = columns.reduce((acc, curr) => {
    return { ...acc, [curr.field]: curr.type === "boolean" ? false : "" };
  }, {});

  // for join mode we need to get actual query fields which are come after active table
  // so wee need to update columns one more time
  const prevQueryFields = usePrevious(queryFields);

  const shouldUpdate =
    !isEqual(tableConfig, prevConfigRef.current) ||
    !isEqual(queryFields, prevQueryFields);

  // update full configs on load and after save response is returned
  useEffect(() => {
    if (shouldUpdate) {
      const mergedColumns = mergeColumnsAndQuery(
        tableConfig.columns,
        queryFields
      );
      const initialColumns = removeIoSystemColumns(mergedColumns);
      const orderedInitialColumns = orderBy(initialColumns, "sortPriority");

      const nextColumnsConverted = orderedInitialColumns
        .map(applyAccessRules)
        .map((column) => convertColumn(column, handleRowChange, queryFields));
      setColumns(nextColumnsConverted);
      prevConfigRef.current = tableConfig;
      const { columns, datasourceForPermissions, ...previousActiveTableRoot } =
        tableConfig;
      previousActiveTableRoot.permissionsFromDataSourceUuid =
        datasourceForPermissions?.uuid;
      previousActiveTableRootRef.current = previousActiveTableRoot;
      const activeTableRoot = { ...previousActiveTableRoot };

      if (dataSourceUuid && !activeTableRoot.permissionsFromDataSourceUuid) {
        // Let's associate the data source with the active table for permissions
        activeTableRoot.permissionsFromDataSourceUuid = dataSourceUuid;
      }
      setActiveTableRoot(activeTableRoot);
      prevTranslatedConfigRef.current = {
        ...tableConfig,
        columns: nextColumnsConverted,
      };
    }
    function applyAccessRules(column) {
      if (user.role === "tenant_owner") return column;
      const accessUuids = column.displaySettings?.accessGroups;
      if (!accessUuids?.length) return column;
      if (!column.isEditable) return column;
      const hasAccess = user.groups.find((uuid) => accessUuids.includes(uuid));
      return { ...column, isEditable: !!hasAccess };
    }
  }, [
    dataSourceUuid,
    handleRowChange,
    queryFields,
    shouldUpdate,
    tableConfig,
    user.groups,
    user.role,
  ]);

  const updateColumn = (index, newColumn) => {
    const newColumns = columns.map((col, i) => {
      const { queryOnly, ...rest } = newColumn;
      return i === index
        ? convertColumn(rest, handleRowChange, queryFields)
        : col;
    });
    setColumns(newColumns);
    return convertToApiColumns(newColumns);
  };

  const apiColumns = convertToApiColumns(columns);

  const addColumn = (name, type) => {
    const nextColumns = [
      ...columns,
      {
        colId: "temp",
        field: name || "",
        type: type || "string",
        editable: true,
        displaySettings: { activeOnly: true },
        cellEditorParams: {},
      },
    ];
    return setColumns(nextColumns);
  };

  const setAllLocked = () => {
    const nextColumns = columns.map((col) => ({ ...col, editable: false }));
    return setColumns(nextColumns);
  };

  const getColumn = (uuid) => {
    const column = columns.find((c) => c.colId === uuid);
    if (!column) {
      console.error(`No column with UUID '${uuid}' found`);
      return;
    }
    return column;
  };

  function produceColumnByUuid(uuid, produceCallback) {
    setColumns((columns) => {
      const column = columns.find((c) => c.colId === uuid);
      if (!column) {
        console.error(`No column with UUID '${uuid}' found`);
        return columns;
      }
      const updatedColumn = produce(column, produceCallback);
      return updatedColumn && updatedColumn !== column
        ? columns.map((existingColumn) =>
            existingColumn.colId === uuid ? updatedColumn : existingColumn
          )
        : columns;
    });
  }

  const addOption = (uuid, value) => {
    const column = getColumn(uuid);

    column.cellEditorParams.values = [...column.cellEditorParams.values, value];

    const nextColumns = columns.map((col) =>
      col.uuid === uuid ? column : col
    );

    return setColumns(nextColumns);
  };

  function setOptions(uuid, rawOptions) {
    return setColumns((columns) => {
      const column = cloneDeep(columns.find((c) => c.colId === uuid));
      let options;

      if (typeof rawOptions[0] === "object") {
        options = rawOptions.map((option) => option.value);
      } else {
        options = rawOptions;
      }

      column.cellEditorParams.values = options;

      const nextColumns = columns.map((col) =>
        col.colId === uuid ? column : col
      );
      return nextColumns;
    });
  }

  function setAccessGroups(uuid, nextGroups) {
    const nextColumns = columns.map((col) =>
      col.colId === uuid
        ? {
            ...col,
            cellEditorParams: {
              ...(col.cellEditorParams ?? {}),
              accessGroups: nextGroups,
            },
          }
        : col
    );

    return setColumns(nextColumns);
  }

  function setDisplayFormatOverride(uuid, value) {
    const nextColumns = columns.map((col) =>
      col.colId === uuid
        ? {
            ...col,
            cellEditorParams: {
              ...(col.cellEditorParams ?? {}),
              displayFromatOverride: value,
            },
          }
        : col
    );

    return setColumns(nextColumns);
  }
  const nextConfig = { ...activeTableRoot, columns: apiColumns };

  const isActiveTableRootDirty = !isEqual(
    activeTableRoot,
    previousActiveTableRootRef.current
  );
  const areColumnsDirty = !!(
    nextConfig.columns.length !== 0 &&
    !isEqual(
      columns.map(ignoreVisible),
      prevTranslatedConfigRef.current?.columns
    )
  );
  const isDirty = areColumnsDirty || isActiveTableRootDirty;

  function ignoreVisible(column) {
    const { visible, ...rest } = column;
    return rest;
  }

  function updateConfig(config) {
    prevConfigRef.current = config;
  }

  // Settings Field name validation
  function validateFieldName(colDef) {
    const { field, colId } = colDef;
    const regex = /^\w+( +\w+)*$/;
    let message = null;

    // do not include new field and editing field
    const fields = columns.filter(
      (col) => col.colId !== "temp" && col.colId !== colId
    );

    if (!field) {
      message = "Column name cannot be empty.";
    } else if (field.length < 2) {
      message = "Column name must have at least 2 characters.";
    } else if (!regex.test(field)) {
      message = `Column name cannot contain only spaces, end or start with space or non alphanumeric symbols`;
    } else if (fields.some((col) => col.field === field)) {
      message = "That column name already exists.";
    }

    setColumnError("name", message);
  }

  return {
    nextConfig,
    columns,
    apiColumns,
    updateColumn,
    setColumns,
    defaultRow,
    addColumn,
    addOption,
    setOptions,
    produceColumnByUuid,
    setAllLocked,
    setAccessGroups,
    isDirty,
    updateConfig, // setUpdateConfig
    prevConfig: prevConfigRef.current,
    validateFieldName,
    convertToApiColumns,
    columnErrorsObject,
    setColumnError,
    setDisplayFormatOverride,
    dataSourceForPermissionsSet:
      !!previousActiveTableRootRef.current.permissionsFromDataSourceUuid,
  };
}

export function convertToApiColumns(cols) {
  return cols.map((col, i) => {
    const {
      field,
      editable,
      colId,
      cellEditor,
      cellEditorParams,
      cellClass,
      displayName,
      ...rest
    } = col;
    let type = col.type;
    let displaySettings = {
      accessGroups: cellEditorParams?.accessGroups || [],
      activeOnly: !!col.displaySettings?.activeOnly,
      displayFromatOverride: cellEditorParams?.displayFromatOverride,
      ...pick(cellEditorParams, displaySettingsProperties),
    };

    let valueOptions = [];
    if (type === "select") {
      type = "string";
      displaySettings.editMode = "select";
      valueOptions = (col.cellEditorParams.values ?? []).map((v) => ({
        value: v,
      }));
    }

    const {
      headerName,
      joinedOverride,
      queryOnly,
      headerClass,
      cellStyle,
      cellRenderer,
      ...remaining
    } = rest;

    const ret = {
      ...remaining,
      name: col.field,
      isEditable: col.editable,
      uuid: col.colId !== "temp" ? col.colId : undefined,
      type,
      displaySettings,
      valueOptions,
      sortPriority: i,
    };

    if (ret.width) {
      ret.displaySettings.width = ret.width;
    } else {
      delete ret.displaySettings.width;
    }

    if (col.queryOnly) {
      ret.etlOnly = true;
      ret.isEditable = false;
      ret.isNullable = false;
      ret.type = null;
      ret.precision = null;
      ret.defaultValue = null;
      ret.valueOptions = null;
    }

    return ret;
  });
}

export const systemColumns = [
  "uuid",
  "io_created_at",
  "io_updated_at",
  "IOUUID",
  "IOLastUpdatedBy",
  "IOLastUpdated",
  "IOCreated",
  "IOCreatedBy",
  "IOEffectiveStart",
  "IOEffectiveEnd",
];

const loadFromDataProperties = [
  "loadFromData",
  "loadFromQueryUuid",
  "loadFromFieldName",
];

const displaySettingsOtherProperties = ["displayName"];

const displaySettingsProperties = [
  ...loadFromDataProperties,
  ...displaySettingsOtherProperties,
];

function removeIoSystemColumns(columns) {
  return columns
    .filter((c) => !systemColumns.find((s) => s === c.name))
    .map((c) => ({ ...c, field: c.name }));
}

export const noLocalSortingComparator = () => 0;

export const convertColumn = (column, handleRowChange, queryFields) => {
  const displaySettings = cloneDeep(column.displaySettings ?? {});
  if (displaySettings.displayFromatOverride === "default") {
    delete displaySettings.displayFromatOverride;
  }
  const accessGroups =
    displaySettings.accessGroups ?? column.cellEditorParams?.accessGroups ?? [];

  const displayFromatOverride =
    displaySettings.displayFromatOverride ??
    column.cellEditorParams?.displayFromatOverride;

  const isEditEnabled = !!(column.isEditable || column.editable);

  const { mapping } =
    (queryFields ?? []).find(
      (field) => field.name === column.name || field.name === column.field
    ) ?? {};
  const displayName = mapping?.displayName ?? column.field;

  const values = column.cellEditorParams?.values?.length
    ? column.cellEditorParams.values
    : column.valueOptions?.length
    ? column.valueOptions.map((v) => v.value)
    : [];

  /** @type ColDef */
  const setting = {
    field: column.field,
    editable: isEditEnabled, // To work for FE and API configs
    type:
      column?.displaySettings?.editMode === "select" ? "select" : column.type,
    colId: column.colId || column.uuid,
    headerName: displayName,
    cellEditorParams: {
      accessGroups,
      displayFromatOverride,
      handleChange: handleRowChange,
      values: values.filter((v) => v !== "None..."),
      ...pick(displaySettings, displaySettingsProperties),
    },
    displaySettings,
  };
  if (column.queryOnly || column.etlOnly) setting.queryOnly = true;
  if (column.joinedOverride) setting.joinedOverride = true;
  if (displaySettings.width) {
    setting.width = displaySettings.width;
  }

  return setting;
};

/**
 * Converts the Active Table Column form format to the actual
 * ColDef format used by Active Grid.
 *
 * @param col
 * @param options {?object|null=}
 * @param options.loadValues {Function=}
 * @param options.loadValuesSearch {Function=}
 * @param options.clientSideSorting {boolean=}
 * @returns {{[p: string]: *}|*}
 */
export function amendColumnForActiveGrid(col, options) {
  const {
    loadValues,
    loadValuesSearch,
    activeView,
    theme,
    rowData,
    clientSideSorting,
  } = options ?? {};
  const serverSideSorting = !clientSideSorting;
  // We need to get rid of all properties that ag-react doesn't recognize.
  const column = omit(cloneDeep(col), [
    "joinedOverride",
    "queryOnly",
    "displaySettings",
  ]);

  const pinnedColumns = activeView?.displaySettings?.pinnedColumns ?? [];
  const pinItem = pinnedColumns.find((pin) => pin.colId === column.colId);

  if (pinItem) {
    column.pinned = pinItem.pinned;
  }

  column.enableCellChangeFlash = true;

  if (!column.width) {
    // This is needed so then when switching a column's column width back to
    // "Default", the column's width would appear to change to default
    // width in Active Grid.
    column.width = 200;
  }

  const cellEditorParams = column.cellEditorParams ?? {};
  column.headerName = `${column.editable ? "✏️ " : ""}${
    cellEditorParams.displayName || col.headerName
  }`;

  if (cellEditorParams.displayFromatOverride === "default") {
    delete cellEditorParams.displayFromatOverride;
  }

  column.type = cellEditorParams.displayFromatOverride ?? column.type;

  if (isActiveTableColumnTypeFile(column.type)) {
    // When sorting by file columns, we don't care about the actual filenames,
    // only about whether there is a file or not.
    column.comparator = fileComparator;
    if (cellEditorParams.displayFromatOverride) {
      // If the file type was a display format override, ensure it's
      // not editable, otherwise the API will potentially reject files
      // due to validation.
      column.editable = false;
    }
  }
  if (serverSideSorting) {
    // Forcing all cells to do this makes it so local sorting wouldn't change
    // anything. This is good, because on local sort immediately the API is
    // called to get the server-side sorted data anyway.
    column.comparator = noLocalSortingComparator;
  }

  const displayFromatOverride = cellEditorParams.displayFromatOverride;

  const type = displayFromatOverride ?? column.type;

  //column.cellEditorParams.values = new Set("");

  switch (type) {
    case "api-image":
    case "api-pdf":
    case "api-excel":
    case "api-word":
    case "api-document":
    case "api-document-or-image":
      return merge(column, getCommonFileRendererProperties());
    case "date":
      return {
        ...column,
        cellEditor: "agDateStringCellEditor",
        valueGetter(params) {
          const value = params.data[params.colDef.field];
          return value === "" ? null : value;
        },
        valueFormatter: (params) => {
          if (params.value && isDate(params.value)) {
            return format(parseISO(params.value), activeTableDateFormatString);
          }
          return params.value;
        },
      };
    case "select":
      return {
        ...column,
        cellEditor: cellEditorParams.loadFromData
          ? ActiveTableDropdown
          : "agSelectCellEditor",
        ...(cellEditorParams.loadFromData
          ? { cellClass: "no-overflow-hidden" }
          : {}),
        cellEditorParams: {
          ...cellEditorParams,
          ...pick(cellEditorParams, displaySettingsProperties),
          ...(cellEditorParams.loadFromData
            ? {
                loadValues,
                loadValuesSearch,
              }
            : {}),
          values: [...new Set(["None...", ...cellEditorParams.values])],
        },
      };
    case "percent":
    case "percent-whole":
    case "percent-whole-2":
    case "percent-round":
    case "percent-tenth":
    case "percent-hundredth":
    case "currency":
    case "currency-whole":
    case "currency-tenth":
    case "integer":
    case "decimal":
    case "decimal-tenth":
    case "date-active-table":
    case "datetime":
      const precision = type === "percent" ? 0 : undefined;

      return {
        ...column,
        valueFormatter: (params) => {
          if (!params?.value && params?.value !== 0) {
            return "--";
          }

          return formatter(params.value ?? "", type, precision);
        },
        headerClass: "ag-right-aligned-header",
        cellStyle: (params) => {
          const color = getActiveTableCellStyleColor(
            params,
            activeView,
            theme,
            rowData
          );

          return {
            textAlign: "right",
            ...(color && { color }),
          };
        },
      };

    case "text":
      return {
        ...column,
        cellEditor: "agTextCellEditor",
        cellStyle: (params) => {
          const color = getActiveTableCellStyleColor(
            params,
            activeView,
            theme,
            rowData
          );

          return {
            ...(color && { color }),
          };
        },
      };
    case "string":
      return {
        ...column,
        cellEditor: "agTextCellEditor",
        cellStyle: (params) => {
          const color = getActiveTableCellStyleColor(
            params,
            activeView,
            theme,
            rowData
          );

          return {
            ...(color && { color }),
          };
        },
      };
    case "boolean":
      return {
        ...column,
        cellEditor: "agCheckboxCellEditor",
        cellRenderer: "agCheckboxCellRenderer",
        cellStyle: { display: "flex", justifyContent: "center" },
      };
    default:
      return column;
  }

  function getCommonFileRendererProperties() {
    return {
      cellEditorPopup: true,
      cellEditorPopupPosition: "over",
      headerClass: "ag-center-aligned-header",
      cellEditor: ActiveTableFileEditor,
      cellRenderer: FileRenderer,
    };
  }
}

export function prependControlsColumn(colDefs, options = {}) {
  const { deleteRow } = options;
  const controls = [];

  if (deleteRow) {
    controls.push(function DeleteActiveTableRowRenderer({ data }) {
      return <DeleteActiveTableRow deleteRow={deleteRow} data={data} />;
    });
  }

  if (!controls.length) {
    return colDefs;
  }

  // The delete column will always be placed at the first position on the left.
  const moveLeft = colDefs.some((col) => col.pinned === "left");

  /** @type ColDef */
  const controlColumn = {
    type: "string",
    colId: "__deleteRow",
    initialWidth: 0,
    width: 44,
    resizable: false,
    cellRenderer: RowControls,
    cellRendererParams: {
      controls,
    },
    // The delete column will always be placed at the first position on the left.
    pinned: moveLeft ? "left" : null,
  };

  return [controlColumn, ...colDefs];
}

export function fileComparator(fileValueA, fileValueB) {
  const valueA = fileValueA !== null;
  const valueB = fileValueB !== null;
  if (valueA === valueB) return 0;
  return valueA > valueB ? 1 : -1;
}

export function getActiveTableCellStyleColor(
  params,
  activeView,
  theme,
  rowData
) {
  const edges = activeView?.displaySettings?.coloredColumnEdges ?? [];
  const edge = edges.find((e) => e.columnName === params.colDef.field);

  return getColoredColumnEdgesByOperator(
    edge,
    params.value,
    theme,
    rowData ?? []
  );
}
